import { useCallback, useEffect, useState } from 'react'
import { Trace } from '../lib/trace'
import { api, unlock, type Child } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Mascot } from './Mascot'
import { sfx } from '../lib/sound'

/**
 * The PIN pad a child sees before their own part of the app opens, when the
 * family has more than one child.
 *
 * Big keys, four dots, and the child's own name and picture at the top, so
 * there is no doubt whose door this is. Digits can be typed on a keyboard too.
 *
 * **Checked on the server**, never in the browser: the browser does not have
 * the PIN to compare with, only a yes or no. After five wrong tries the server
 * pauses that child for a minute, and this says so in a child's words.
 *
 * Sprout looks curious, never cross. A wrong PIN shakes the dots and clears
 * them; it does not scold.
 *
 * **The right PIN earns a ticket** (D-065), kept by `unlock` in api.ts and
 * sent with this child's requests. The server checks the ticket; this pad
 * only collects the digits. "Forgot your PIN?" fetches a grown-up.
 */
export function PinPad({
  child, onUnlocked, onCancel, onForgot,
}: {
  child: Child
  onUnlocked: () => void
  onCancel: () => void
  onForgot?: () => void
}) {
  const [digits, setDigits] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [shake, setShake] = useState(false)

  const submit = useCallback(async (pin: string) => {
    setBusy(true)
    try {
      const { ticket } = await api.unlockChild(child.learner_id, pin)
      unlock.setChild(child.learner_id, ticket)
      sfx.correct()
      onUnlocked()
    } catch (err) {
      const detail = (err as { detail?: string } | null)?.detail
      setMessage(detail || 'That did not work. Try again.')
      setShake(true)
      window.setTimeout(() => setShake(false), 450)
      setDigits('')
    } finally {
      setBusy(false)
    }
  }, [child.learner_id, onUnlocked])

  // Submitting happens here, not inside a state updater. React runs updaters
  // twice in development, which would check the PIN twice — and two wrong
  // checks count as two of the five tries.
  const press = useCallback((d: string) => {
    if (busy || digits.length >= 4) return
    setMessage(null)
    const next = digits + d
    setDigits(next)
    if (next.length === 4) void submit(next)
  }, [busy, digits, submit])

  const back = useCallback(() => setDigits((cur) => cur.slice(0, -1)), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) press(e.key)
      else if (e.key === 'Backspace') back()
      else if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [press, back, onCancel])

  const key = (label: string, action: () => void, aria?: string) => (
    <button
      key={label}
      type="button"
      onClick={action}
      disabled={busy}
      aria-label={aria ?? label}
      style={{
        height: 64, minWidth: TAP_MIN, borderRadius: radius.xl,
        border: `2px solid ${theme.borderWarm}`, background: theme.card,
        fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 600,
        color: theme.text, cursor: 'pointer',
        boxShadow: `0 3px 0 ${theme.borderWarm}`,
      }}
    >{label}</button>
  )

  return (
    <div role="dialog" aria-modal="true" aria-label={`${child.display_name}'s PIN`} style={{
      position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(21,32,43,0.45)',
      display: 'grid', placeItems: 'center', padding: 16,
    }}>
      <style>{`@keyframes ws-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-10px)}
        40%{transform:translateX(9px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(4px)} }`}</style>
      <div style={{
        background: theme.bg, borderRadius: 24, padding: '20px 20px 18px',
        width: '100%', maxWidth: 340, textAlign: 'center',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: 4 }}>
          <Mascot size={64} mood={message ? 'wonder' : 'happy'} label="" />
        </div>
        <h2 style={{ margin: '4px 0 2px', fontSize: 24, color: theme.text }}>
          Hi, {child.display_name}!
        </h2>
        <p style={{ margin: '0 0 14px', color: theme.muted, fontSize: 16 }}>
          <Trace id="T25.pin_hash" title="checked on the server; a right PIN saves a ticket to T28 access_grant">Type your PIN.</Trace>
        </p>

        <div style={{
          display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 10,
          animation: shake ? 'ws-shake 0.42s ease' : undefined,
        }}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} style={{
              width: 18, height: 18, borderRadius: 999,
              background: i < digits.length ? theme.green : 'transparent',
              border: `2.5px solid ${i < digits.length ? theme.green : theme.border}`,
            }} />
          ))}
        </div>
        <p role="status" style={{ minHeight: 22, margin: '0 0 10px', color: theme.orangeDark, fontSize: 15, fontWeight: 700 }}>
          {message ?? ''}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => key(d, () => press(d)))}
          {key('←', back, 'Delete')}
          {key('0', () => press('0'))}
          <span />
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
          {onForgot ? (
            <button type="button" onClick={onForgot} style={{
              background: 'transparent', border: 'none', color: theme.blue,
              fontSize: 15, fontWeight: 700, cursor: 'pointer', minHeight: TAP_MIN,
            }}>Forgot your PIN?</button>
          ) : null}
          <button type="button" onClick={onCancel} style={{
            background: 'transparent', border: 'none', color: theme.muted,
            fontSize: 15, fontWeight: 700, cursor: 'pointer', minHeight: TAP_MIN,
          }}>Not me — go back</button>
        </div>
      </div>
    </div>
  )
}
